import { Attachment, TasksInListSet } from 'src/app/tasks/shared/models';
import {
  AdminUser,
  Editor2Answer,
  Editor2Attachment,
  MessageSender,
  MessageSenderForLogAnswer,
  ShopProductInfo,
  Site,
  TagGroup,
  TaskState,
  TaskStateAndAdminUser,
  ValueAndUnit,
} from '../../shared/models/misc.model';
import { WhatsappTemplate } from './models';

export interface TaskOrActionSet {
  [id: string]: AnyTaskOrActionType; // '<id>:<type>' -> TaskOrAction
}

export enum TaskOrActionType {
  Annotation = 'annotation',
  Answer = 'answer',
  AssignTo = 'assign-to',
  HelpAnswer = 'help-answer',
  HelpRequest = 'help-request',
  InmessageCarousel = 'inmessage-carousel',
  Message = 'message',
  Modified = 'modified',
  SendConversation = 'send-conversation',
  Steal = 'steal',
  Tag = 'tag',
  Task = 'task',
  WhatsappTemplate = 'whatsapp-template',
  KakaotalkTemplate = 'kakaotalk-template',
  PutPendingByBot = 'put-pending-by-bot',
}

export type AnyTaskOrActionType = Task | Message | AnswerAction | AnnotationOrHelpAction | PublicContextMessage;

interface TaskOrAction {
  id: string; // '<id>:<type>'
  type: TaskOrActionType;
  datetime: string;

  // UI-only properties
  dayIndicatorFormat?: string; // if null, no day indicator will be displayed above the message
  content?: NormalizedTaskOrActionContent; // text and attachments (if any), in a common format for all types of TaskOrActions

  translatedText?: string; // For the task actions modal
}

export interface NormalizedTaskOrActionContent {
  text: string;
  attachments: Editor2Attachment[];
}

export interface TaskOrActionWithMessage {
  fromUserAvatarUrl: string;
  isPrivate: boolean;
  isDeleted: boolean;
  isAnonymized: boolean;
  externalUrl?: string; // URL on social network, for public messages only
  isHidden?: boolean; // only public messages on some networks can be hidden
  quotedMessageExternalId: number;
}

export interface Task extends TaskOrAction, TaskOrActionWithMessage {
  taskStateCode: TaskState;
  adminUser: AdminUser;
  text: string;
  attachments: Attachment[];
  detailedTaskType: string;
  rating?: {
    value: number;
    scale: number;
  };
  latestAnswerActionId?: number; // present if the task has been replied at least once
  shopProductInfo?: ShopProductInfo;
  tagIds?: number[]; // present if we are reloading the processed task following a websocket notification
  routingTagId?: number;
  // Flag to indicate a conversation is closed. Specific to Apple Messages.
  closed?: boolean;
}

// message sent outside of Alcméon
export interface Message extends TaskOrAction, TaskOrActionWithMessage {
  text: string;
  attachments: Attachment[];
  isFromBrand: boolean;
}

export interface PublicContextMessage extends Message {
  from: MessageSender;
  detailedTaskType?: string; // only present if the message has a corresponding task

  // fields set by the front end
  level: number; // 0, 1 or 2, depending on that message depth in the tree of context message. Only makes sense for FB public
  timeDelta: ValueAndUnit;
}

interface ActionAutoCause {
  rule_id: number;
  code: string;
  title: string;
  handover_protocol: boolean;
}

export interface GenericAction extends TaskOrAction {
  adminUser: AdminUser;
  cause?: ActionAutoCause;
}

export interface AnswerAction extends GenericAction, TaskOrActionWithMessage {
  answer: Editor2Answer[];
  detailedTaskType: string;
  answeredTaskId: number;
  error?: AnswerError;
  firstAnswerByHumanDelaysInSeconds?: {
    raw: number;
    businessHours: number;
  };

  // set by the UI
  firstAnswerByHumanDelays?: {
    raw?: ValueAndUnit[];
    businessHours?: ValueAndUnit[];
  };
}

export interface AnnotationOrHelpAction extends GenericAction {
  text: string;
  helpRequestId?: number;
}

export interface StealOrAssignAction extends GenericAction {
  targetAdminUser: AdminUser;
}

export interface TagAction extends GenericAction, ModifiedAction, AnswerAction {
  isRouting: boolean;
  tagGroupName: string; // currently only displayed for non-routing tag
  tagName: string;
  /**
   * The action cause translation computed on the fly.
   * TODO move into the service.
   */
  extraTranslatedText?: string;
}

export interface ModifiedAction extends GenericAction {
  newContent: string;

  updatedTask: Task; // set by the UI
}

interface AnswerError {
  id: number;
  translationKey: string;
  details: string;
  logAnswerRequest: BackendLogAnswerRequest;
}

export interface TaskForProcessingRequest {
  taskId: number;
  formatFirstNMessages: number;
}

interface TaskStateAndAdminUserAndPreviousState extends TaskStateAndAdminUser {
  previousStateCode?: TaskState; // only set for 'assigned' tasks
}

export interface ProcessedTaskInfo {
  id: string; // '<id>:<type>'
  messageId: number;
  tagIds: number[];
  routingTagId?: number; // I think at least one company doesn't use routing tags
  taskTypeInt: number;
  isPrivate: boolean;
  isAnonymized: boolean;
  replyToList: MessageSender[];
  replyAsConfig: ReplyAsConfig;
  canSwitchFromPublicToPrivate: boolean;
  mustSwitchFromPublicToPrivate: boolean;
  isTooOldToReply: {
    canReply: boolean;
    reason: string; // translation key
  };
  state: TaskStateAndAdminUserAndPreviousState;
  manuallySendableSurveys: ManuallySensableSurveys;
  shouldCallUpdateDeletedStatus: boolean;
  customFieldContext: CustomFieldsContext;
  updateDeletedStatusError?: string;
}

export interface CustomFieldsContext {
  customFieldConversationId: number;
  customFieldUserId: number;
}

interface ReplyAsConfig {
  intialReplyAsSite: Site; // usually the default_reply_as site, unless the logged user can't answer on it
  allowedReplyAsSites: Site[]; // at least the procesed task's site
}

interface ManuallySensableSurveys {
  hasManuallySendableSurvey: boolean;
  tagRestrictions: TagRestriction[][]; // one list of TagRestriction for each manually-sendable survey
}

export interface TagRestriction {
  tagGroupId: number;
  tagIds: number[]; // if not empty, the task's corresponding tag group must have a value among these for the survey to be sendable
}

export interface TaskForProcessingResponse {
  from: MessageSender;
  site: Site;
  tagGroups: TagGroup[]; // all available tag groups and their tags
  routingTagGroup?: TagGroup; // I think at least one company doesn't use routing tags
  tasksOrActionsIds: string[]; // '<id>:<type>'
  tasksOrActionsById: TaskOrActionSet;
  processingTask: ProcessedTaskInfo;
  previousOnHoldNotByBotIds: string[]; // ['<id>:task']
  isWhatsappTemplateModeMandatory?: boolean;
  whatsappTemplates?: WhatsappTemplate[];
  isLinkShortenerEnabled: boolean;

  // added by the service for convenience
  allAnnotationIds: string[]; // '<id>:<type>'
  allHelpRequestAndResponseIds: string[]; // '<id>:<type>'

  // added by the 1to1Engagement feature for convenience.
  engagement?: boolean;
}

export interface LazyloadTasksOrActionsRequest {
  tasksOrActionsIds: string[]; // '<id>:<type>'[]
  includeTags: boolean;
}

export interface LazyloadTasksOrActionsResponse {
  tasksOrActionsById: TaskOrActionSet;
}

export interface LogAnswerRequest {
  taskId: number;
  replyAs: { id: number };
  replyTo: MessageSenderForLogAnswer;
  replyToUserIds: number[];
  isPrivate: boolean;
  answers: Editor2Answer[];
}

// For answers action that didn't succeed,
// the backend passes us the request we sent to it
// (so that the user can retry sending his answer)
export interface BackendLogAnswerRequest {
  reply_as: { id: number };
  reply_to: MessageSenderForLogAnswer;
  reply_to_user_ids: number[];
  is_private: boolean;
  answer: Editor2Answer[];
}

export interface ReleaseTaskRequest {
  taskId: number;
}

export type ReleaseTaskResponse = TasksInListSet;

export interface TaskPublicContextResponse {
  contextMessageInfos: ContextMessageInfo[];
}

export interface ContextMessageInfo {
  id: number;
  parentId?: number;
  datetime: string; // ISO 8601
  isProcessedTask?: boolean; // only set if true

  level?: number; // computed by the front-end
  children?: ContextMessageInfo[]; // computed by the front-end
}

export interface LazyloadPublicContextMessageResponse {
  contextMessagesById: { [id: string]: PublicContextMessage };
}

export enum AfterSendActionType {
  None, // nothing special to be done apart from sending the answer
  PutOnHold,
  Route,
  TriggerSurvey,
  AskForHelp,
}

export interface AfterSendAction {
  type: AfterSendActionType;
  details?: { tagIds?: number[]; message?: string };
}

export interface AnswersAndAfterSendAction {
  answers: Editor2Answer[];
  afterSendAction: AfterSendAction;
}
